/* Created by Interactive 63 - https://interactive63.com */
/**
 * Last-resort error reporting. Anything that escapes the editor's own handling is logged to the
 * console and shown as one non-blocking toast; stack traces never reach the interface. Work is
 * autosaved separately, so the message can reassure rather than alarm.
 */
import { toast } from './ui/toast';

const MESSAGE = 'Something went wrong. Your work is autosaved.';
const QUIET_PERIOD_MS = 5000;

/** Browsers raise this harmless notice when a layout observer needs another frame. */
function isBenign(message: string): boolean {
  return message.startsWith('ResizeObserver loop');
}

/** Installs the global handlers and returns a function that removes them. */
export function installErrorHandlers(): () => void {
  let lastShown = Number.NEGATIVE_INFINITY;

  /** Logs the problem and shows the toast, at most once per quiet period. */
  const report = (problem: unknown): void => {
    console.error(problem);
    const now = Date.now();
    if (now - lastShown < QUIET_PERIOD_MS) return;
    lastShown = now;
    toast.error(MESSAGE);
  };
  const onError = (event: ErrorEvent): void => {
    if (isBenign(event.message)) return;
    report(event.error ?? event.message);
  };
  const onRejection = (event: PromiseRejectionEvent): void => {
    report(event.reason);
  };
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
